// =============================================================================
// Turnstile.jsx — the Cloudflare Turnstile widget for the login/reset forms
// =============================================================================
// Loads Turnstile the same way the app loads Google Maps / jsPDF: a <script> tag
// injected at runtime (no npm dependency, so no lockfile regeneration). Renders
// the widget EXPLICITLY via window.turnstile.render so we control its lifecycle
// and can force a fresh token (Turnstile tokens are single-use, ~5 min TTL).
//
// INERT when no site key: renders null and loads nothing, so a build without
// VITE_TURNSTILE_SITE_KEY is byte-for-byte today's login. See services/captcha.ts.
//
// Props:
//   onToken(token)  — called with the token on success; "" on expiry/error.
//   resetKey        — change this number to tear down + re-render the widget
//                     (used after a consumed sign-in attempt to get a new token).
// =============================================================================

import { useEffect, useRef } from "react";
import { CAPTCHA_SITE_KEY } from "../../services/captcha";

const SCRIPT_SRC = "https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit";

// Module-level singleton so the script is injected at most once across mounts.
let scriptPromise = null;
function loadTurnstileScript() {
  if (typeof window !== "undefined" && window.turnstile) return Promise.resolve();
  if (scriptPromise) return scriptPromise;
  scriptPromise = new Promise((resolve, reject) => {
    const s = document.createElement("script");
    s.src = SCRIPT_SRC;
    s.async = true;
    s.defer = true;
    s.onload = () => resolve();
    s.onerror = () => {
      scriptPromise = null; // allow a later retry
      reject(new Error("Turnstile script failed to load"));
    };
    document.head.appendChild(s);
  });
  return scriptPromise;
}

export function Turnstile({ onToken, resetKey = 0 }) {
  const containerRef = useRef(null);
  const widgetIdRef = useRef(null);

  useEffect(() => {
    if (!CAPTCHA_SITE_KEY) return undefined; // inert until configured
    let cancelled = false;

    function removeWidget() {
      if (widgetIdRef.current && window.turnstile) {
        try {
          window.turnstile.remove(widgetIdRef.current);
        } catch {
          /* widget already gone */
        }
      }
      widgetIdRef.current = null;
    }

    loadTurnstileScript()
      .then(() => {
        if (cancelled || !containerRef.current || !window.turnstile) return;
        removeWidget(); // clear any prior instance (resetKey change / remount)
        widgetIdRef.current = window.turnstile.render(containerRef.current, {
          sitekey: CAPTCHA_SITE_KEY,
          callback: (token) => onToken?.(token),
          "expired-callback": () => onToken?.(""),
          "error-callback": () => onToken?.(""),
        });
      })
      .catch(() => {
        // Script blocked/unreachable: stay inert. The submit guard keeps the
        // user on the form with the "complete the verification" message.
      });

    return () => {
      cancelled = true;
      removeWidget();
    };
  }, [onToken, resetKey]);

  if (!CAPTCHA_SITE_KEY) return null;
  return <div ref={containerRef} style={{ marginBottom: 14 }} />;
}
